import { useEffect, useState } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Tag,
  message,
  Row,
  Col,
  Statistic,
  Tabs,
  Tree,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UserOutlined,
  TeamOutlined,
  KeyOutlined,
  SafetyOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { DataNode } from 'antd/es/tree'
import { confirmDelete, confirmStatusChange, confirmDanger } from '../../hooks/useConfirm'
import { adminAPI } from '../../api'

const { Option } = Select

interface Admin {
  id: number
  username: string
  realName: string
  phone: string
  email: string
  roleId: number
  roleName: string
  status: boolean
  lastLoginTime: string
  lastLoginIp: string
  createdAt: string
}

interface Role {
  id: number
  name: string
  description: string
  permissions: string[]
  adminCount: number
  status: boolean
  isSystem: boolean
  createdAt: string
}

interface PermissionNode extends DataNode {
  key: string
  title: string
  children?: PermissionNode[]
}

// 权限配置
const PERMISSIONS: PermissionNode[] = [
  {
    key: 'dashboard',
    title: '仪表盘',
    children: [
      { key: 'dashboard:view', title: '查看仪表盘' },
    ],
  },
  {
    key: 'products',
    title: '商品管理',
    children: [
      { key: 'products:view', title: '查看商品' },
      { key: 'products:create', title: '添加商品' },
      { key: 'products:edit', title: '编辑商品' },
      { key: 'products:delete', title: '删除商品' },
      { key: 'products:status', title: '上下架商品' },
    ],
  },
  {
    key: 'price',
    title: '价格日期',
    children: [
      { key: 'price:view', title: '查看价格' },
      { key: 'price:edit', title: '编辑价格' },
      { key: 'price:stock', title: '编辑库存' },
      { key: 'price:batch', title: '批量操作' },
    ],
  },
  {
    key: 'orders',
    title: '订单管理',
    children: [
      { key: 'orders:view', title: '查看订单' },
      { key: 'orders:detail', title: '查看订单详情' },
      { key: 'orders:status', title: '修改订单状态' },
      { key: 'orders:deliver', title: '发货' },
      { key: 'orders:cancel', title: '取消订单' },
      { key: 'orders:verify', title: '审核支付凭证' },
    ],
  },
  {
    key: 'members',
    title: '会员管理',
    children: [
      { key: 'members:view', title: '查看会员' },
      { key: 'members:create', title: '添加会员' },
      { key: 'members:edit', title: '编辑会员' },
      { key: 'members:delete', title: '删除会员' },
      { key: 'members:level', title: '修改等级' },
      { key: 'members:reset', title: '重置密码' },
    ],
  },
  {
    key: 'coupons',
    title: '优惠券管理',
    children: [
      { key: 'coupons:view', title: '查看优惠券' },
      { key: 'coupons:create', title: '创建优惠券' },
      { key: 'coupons:edit', title: '编辑优惠券' },
      { key: 'coupons:delete', title: '删除优惠券' },
      { key: 'coupons:grant', title: '发放优惠券' },
    ],
  },
  {
    key: 'statistics',
    title: '数据报表',
    children: [
      { key: 'statistics:view', title: '查看报表' },
      { key: 'statistics:export', title: '导出数据' },
    ],
  },
  {
    key: 'settings',
    title: '系统设置',
    children: [
      { key: 'settings:home', title: '首页配置' },
      { key: 'settings:delivery', title: '配送与运费' },
      { key: 'settings:admin', title: '管理员管理' },
      { key: 'settings:role', title: '角色管理' },
    ],
  },
]

const AdminManagement = () => {
  const [activeTab, setActiveTab] = useState('admin')
  const [loading, setLoading] = useState(false)

  // 管理员相关
  const [admins, setAdmins] = useState<Admin[]>([])
  const [adminModalVisible, setAdminModalVisible] = useState(false)
  const [editingAdmin, setEditingAdmin] = useState<Admin | null>(null)
  const [adminForm] = Form.useForm()
  const [searchKeyword, setSearchKeyword] = useState('')
  const [adminPagination, setAdminPagination] = useState({ current: 1, pageSize: 10, total: 0 })
  const [adminSubmitting, setAdminSubmitting] = useState(false)

  // 角色相关
  const [roles, setRoles] = useState<Role[]>([])
  const [roleModalVisible, setRoleModalVisible] = useState(false)
  const [editingRole, setEditingRole] = useState<Role | null>(null)
  const [roleForm] = Form.useForm()
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([])
  const [expandedKeys, setExpandedKeys] = useState<string[]>(['dashboard', 'products', 'price', 'orders', 'members', 'coupons', 'statistics', 'settings'])
  const [roleSubmitting, setRoleSubmitting] = useState(false)

  // 重置密码相关
  const [passwordModalVisible, setPasswordModalVisible] = useState(false)
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null)
  const [passwordForm] = Form.useForm()
  const [passwordSubmitting, setPasswordSubmitting] = useState(false)

  // 统计数据
  const [stats, setStats] = useState({
    totalAdmins: 0,
    activeAdmins: 0,
    totalRoles: 0,
  })

  useEffect(() => {
    fetchAdmins()
    fetchRoles()
  }, [])

  useEffect(() => {
    fetchAdmins()
  }, [searchKeyword, adminPagination.current, adminPagination.pageSize])

  const fetchAdmins = async () => {
    setLoading(true)
    try {
      const params: any = {
        page: adminPagination.current,
        pageSize: adminPagination.pageSize,
      }
      if (searchKeyword) params.keyword = searchKeyword

      const res = await adminAPI.getAdmins(params)
      if (res.code === 200) {
        setAdmins(res.data.list || [])
        setAdminPagination(prev => ({ ...prev, total: res.data.total || 0 }))
        setStats(prev => ({
          ...prev,
          totalAdmins: res.data.total || 0,
          activeAdmins: (res.data.list || []).filter((a: Admin) => a.status).length,
        }))
      }
    } catch (error) {
      // 错误由 request 拦截器统一处理
    } finally {
      setLoading(false)
    }
  }

  const fetchRoles = async () => {
    try {
      const res = await adminAPI.getRoles()
      if (res.code === 200) {
        setRoles(res.data || [])
        setStats(prev => ({
          ...prev,
          totalRoles: res.data?.length || 0,
        }))
      }
    } catch (error) {
      // 错误统一处理
    }
  }

  // ========== 管理员管理 ==========
  const handleAddAdmin = () => {
    setEditingAdmin(null)
    adminForm.resetFields()
    adminForm.setFieldsValue({ status: true })
    setAdminModalVisible(true)
  }

  const handleEditAdmin = (record: Admin) => {
    setEditingAdmin(record)
    adminForm.setFieldsValue(record)
    setAdminModalVisible(true)
  }

  const handleDeleteAdmin = (record: Admin) => {
    confirmDelete(record.username + ' 管理员账户', '系统设置', async () => {
      const res = await adminAPI.deleteAdmin(record.id)
      if (res.code === 200) {
        message.success('删除成功')
        fetchAdmins()
      }
    }, '删除后该管理员将无法登录系统。')
  }

  const handleToggleAdminStatus = (record: Admin, status: boolean) => {
    confirmStatusChange(record.username + ' 管理员账户', status, '系统设置', async () => {
      const res = await adminAPI.toggleAdminStatus(record.id, status)
      if (res.code === 200) {
        setAdmins(admins.map(a => a.id === record.id ? { ...a, status } : a))
        message.success(status ? '已启用' : '已禁用')
      }
    })
  }

  const handleAdminSubmit = async () => {
    try {
      const values = await adminForm.validateFields()
      const selectedRole = roles.find(r => r.id === values.roleId)

      setAdminSubmitting(true)
      if (editingAdmin) {
        const res = await adminAPI.updateAdmin(editingAdmin.id, { ...values, roleName: selectedRole?.name })
        if (res.code === 200) {
          setAdmins(admins.map(a => a.id === editingAdmin.id ? res.data : a))
          message.success('更新成功')
        }
      } else {
        const res = await adminAPI.createAdmin({ ...values, roleName: selectedRole?.name })
        if (res.code === 200) {
          setAdmins([res.data, ...admins])
          message.success('添加成功')
        }
      }
      setAdminModalVisible(false)
      fetchAdmins()
    } catch (error) {
      // 错误统一处理
    } finally {
      setAdminSubmitting(false)
    }
  }

  const handleResetPassword = (record: Admin) => {
    setSelectedAdmin(record)
    passwordForm.resetFields()
    setPasswordModalVisible(true)
  }

  const handlePasswordSubmit = async () => {
    if (!selectedAdmin) return
    try {
      const values = await passwordForm.validateFields()

      confirmDanger({
        title: '确认重置密码',
        content: `确定要重置管理员「${selectedAdmin.username}」的密码吗？`,
        okText: '确认重置',
        okType: 'primary',
        module: '系统设置',
        action: '重置密码',
        targetName: selectedAdmin.username,
        onOk: async () => {
          setPasswordSubmitting(true)
          try {
            await adminAPI.resetAdminPassword(selectedAdmin.id, values.newPassword)
            message.success('密码重置成功')
            setPasswordModalVisible(false)
          } finally {
            setPasswordSubmitting(false)
          }
        },
      })
    } catch (error) {
      // 表单验证错误
    }
  }

  // ========== 角色管理 ==========
  const handleAddRole = () => {
    setEditingRole(null)
    roleForm.resetFields()
    setSelectedPermissions([])
    roleForm.setFieldsValue({ status: true })
    setRoleModalVisible(true)
  }

  const handleEditRole = (record: Role) => {
    setEditingRole(record)
    roleForm.setFieldsValue(record)
    setSelectedPermissions(record.permissions || [])
    setRoleModalVisible(true)
  }

  const handleDeleteRole = (record: Role) => {
    confirmDelete(record.name + ' 角色', '系统设置', async () => {
      const res = await adminAPI.deleteRole(record.id)
      if (res.code === 200) {
        message.success('删除成功')
        fetchRoles()
      }
    }, '删除角色将影响所有关联管理员的权限。')
  }

  const handleToggleRoleStatus = (record: Role, status: boolean) => {
    confirmStatusChange(record.name + ' 角色', status, '系统设置', async () => {
      const res = await adminAPI.toggleRoleStatus(record.id, status)
      if (res.code === 200) {
        setRoles(roles.map(r => r.id === record.id ? { ...r, status } : r))
        message.success(status ? '已启用' : '已禁用')
      }
    })
  }

  const handleRoleSubmit = async () => {
    try {
      const values = await roleForm.validateFields()

      setRoleSubmitting(true)
      if (editingRole) {
        const res = await adminAPI.updateRole(editingRole.id, { ...values, permissions: selectedPermissions })
        if (res.code === 200) {
          setRoles(roles.map(r => r.id === editingRole.id ? res.data : r))
          message.success('更新成功')
        }
      } else {
        const res = await adminAPI.createRole({ ...values, permissions: selectedPermissions })
        if (res.code === 200) {
          setRoles([...roles, res.data])
          message.success('创建成功')
        }
      }
      setRoleModalVisible(false)
      fetchRoles()
    } catch (error) {
      // 错误统一处理
    } finally {
      setRoleSubmitting(false)
    }
  }

  const handlePermissionCheck = (checked: any) => {
    const { checkedKeys, halfCheckedKeys } = checked
    setSelectedPermissions([...checkedKeys, ...halfCheckedKeys])
  }

  const adminColumns: ColumnsType<Admin> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '用户名', dataIndex: 'username', key: 'username', width: 120 },
    { title: '真实姓名', dataIndex: 'realName', key: 'realName', width: 100 },
    { title: '手机号', dataIndex: 'phone', key: 'phone', width: 130 },
    { title: '邮箱', dataIndex: 'email', key: 'email', width: 180 },
    { title: '角色', dataIndex: 'roleName', key: 'roleName', width: 120 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (val: boolean, record: Admin) => (
        <Switch checked={val} size="small" onChange={(checked) => handleToggleAdminStatus(record, checked)} />
      ),
    },
    { title: '最后登录', dataIndex: 'lastLoginTime', key: 'lastLoginTime', width: 160 },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: Admin) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditAdmin(record)}>
            编辑
          </Button>
          <Button type="link" size="small" icon={<KeyOutlined />} onClick={() => handleResetPassword(record)}>
            重置密码
          </Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteAdmin(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ]

  const roleColumns: ColumnsType<Role> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: '角色名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (val: string, record: Role) => (
        <span>
          {val}
          {record.isSystem && <Tag color="blue" style={{ marginLeft: 8 }}>系统</Tag>}
        </span>
      ),
    },
    { title: '描述', dataIndex: 'description', key: 'description', width: 200 },
    { title: '管理员数', dataIndex: 'adminCount', key: 'adminCount', width: 80 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (val: boolean, record: Role) => (
        <Switch
          checked={val}
          size="small"
          disabled={record.isSystem && !val}
          onChange={(checked) => handleToggleRoleStatus(record, checked)}
        />
      ),
    },
    { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 120 },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: Role) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditRole(record)}>
            编辑
          </Button>
          {!record.isSystem && (
            <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteRole(record)}>
              删除
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div className="admin-management-page">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card size="small">
            <Statistic title="管理员总数" value={stats.totalAdmins} prefix={<UserOutlined />} />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic title="启用管理员" value={stats.activeAdmins} prefix={<SafetyOutlined />} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic title="角色总数" value={stats.totalRoles} prefix={<TeamOutlined />} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
      </Row>

      <Card size="small">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="管理员列表" key="admin">
            <Space wrap style={{ marginBottom: 16 }}>
              <Input
                placeholder="搜索用户名/姓名/手机号"
                prefix={<SearchOutlined />}
                style={{ width: 250 }}
                value={searchKeyword}
                onChange={e => setSearchKeyword(e.target.value)}
                onPressEnter={fetchAdmins}
              />
              <Button type="primary" icon={<SearchOutlined />} onClick={fetchAdmins}>
                搜索
              </Button>
            </Space>

            <div style={{ marginBottom: 16, textAlign: 'right' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddAdmin}>
                添加管理员
              </Button>
            </div>

            <Table
              columns={adminColumns}
              dataSource={admins}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1300 }}
              pagination={{
                ...adminPagination,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条`,
                onChange: (page, pageSize) => setAdminPagination({ ...adminPagination, current: page, pageSize }),
              }}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="角色管理" key="role">
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddRole}>
                添加角色
              </Button>
            </div>

            <Table
              columns={roleColumns}
              dataSource={roles}
              rowKey="id"
              loading={loading}
              pagination={{ showSizeChanger: true, showTotal: (total) => `共 ${total} 条` }}
            />
          </Tabs.TabPane>
        </Tabs>
      </Card>

      {/* 管理员编辑弹窗 */}
      <Modal
        title={editingAdmin ? '编辑管理员' : '添加管理员'}
        open={adminModalVisible}
        onOk={handleAdminSubmit}
        onCancel={() => setAdminModalVisible(false)}
        confirmLoading={adminSubmitting}
        width={500}
        destroyOnClose
      >
        <Form form={adminForm} layout="vertical">
          <Form.Item name="username" label="用户名" rules={[
            { required: true, message: '请输入用户名' },
            { min: 3, max: 20, message: '用户名长度3-20位' }
          ]}>
            <Input placeholder="请输入用户名" disabled={!!editingAdmin} />
          </Form.Item>
          <Form.Item name="realName" label="真实姓名" rules={[{ required: true, message: '请输入真实姓名' }]}>
            <Input placeholder="请输入真实姓名" />
          </Form.Item>
          <Form.Item name="phone" label="手机号" rules={[
            { required: true, message: '请输入手机号' },
            { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' }
          ]}>
            <Input placeholder="请输入手机号" />
          </Form.Item>
          <Form.Item name="email" label="邮箱" rules={[{ type: 'email', message: '请输入正确的邮箱格式' }]}>
            <Input placeholder="请输入邮箱" />
          </Form.Item>
          <Form.Item name="roleId" label="所属角色" rules={[{ required: true, message: '请选择角色' }]}>
            <Select placeholder="请选择角色">
              {roles.filter(r => r.status).map(role => (
                <Option key={role.id} value={role.id}>{role.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="status" label="状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
          {!editingAdmin && (
            <Form.Item name="password" label="初始密码" rules={[
              { required: true, message: '请输入初始密码' },
              { min: 6, message: '密码至少6位' }
            ]}>
              <Input.Password placeholder="请输入初始密码" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* 重置密码弹窗 */}
      <Modal
        title={`重置密码 - ${selectedAdmin?.realName}`}
        open={passwordModalVisible}
        onOk={handlePasswordSubmit}
        onCancel={() => setPasswordModalVisible(false)}
        confirmLoading={passwordSubmitting}
        width={400}
        destroyOnClose
      >
        <Form form={passwordForm} layout="vertical">
          <Form.Item name="newPassword" label="新密码" rules={[
            { required: true, message: '请输入新密码' },
            { min: 6, message: '密码至少6位' }
          ]}>
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
          <Form.Item name="confirmPassword" label="确认密码" dependencies={['newPassword']} rules={[
            { required: true, message: '请确认密码' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve()
                }
                return Promise.reject(new Error('两次输入的密码不一致'))
              },
            }),
          ]}>
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 角色编辑弹窗 */}
      <Modal
        title={editingRole ? '编辑角色' : '添加角色'}
        open={roleModalVisible}
        onOk={handleRoleSubmit}
        onCancel={() => setRoleModalVisible(false)}
        confirmLoading={roleSubmitting}
        width={600}
        destroyOnClose
      >
        <Form form={roleForm} layout="vertical">
          <Form.Item name="name" label="角色名称" rules={[{ required: true, message: '请输入角色名称' }]}>
            <Input placeholder="请输入角色名称" disabled={editingRole?.isSystem} />
          </Form.Item>
          <Form.Item name="description" label="角色描述">
            <Input.TextArea rows={3} placeholder="请输入角色描述" maxLength={200} showCount />
          </Form.Item>
          <Form.Item label="权限配置" required>
            <div style={{
              maxHeight: 400,
              overflowY: 'auto',
              border: '1px solid #d9d9d9',
              borderRadius: 8,
              padding: 12,
            }}>
              <Tree
                checkable
                defaultExpandAll
                expandedKeys={expandedKeys}
                onExpand={(keys) => setExpandedKeys(keys as string[])}
                checkedKeys={selectedPermissions}
                onCheck={handlePermissionCheck}
                treeData={PERMISSIONS}
              />
            </div>
          </Form.Item>
          <Form.Item name="status" label="状态" valuePropName="checked">
            <Switch
              checkedChildren="启用"
              unCheckedChildren="禁用"
              disabled={editingRole?.isSystem}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default AdminManagement
